import { Link } from "react-router-dom";
import { FeatureOff } from "../FeatureOff";
import { Pill } from "../Pill";
import { Empty, ErrorState } from "../States";
import { apiGet } from "../../lib/http";
import { serverTime } from "../../lib/when";
import { caseFailure, outcomeLabel, STATES, stateLabel, type Case, type CaseState } from "./caseFlow";
import { assetPath } from "./exports";
import { TableScroll } from "../TableScroll";

export interface CaseFilters {
  state: CaseState | null;
  package: string | null;
}

/** The most one list returns; the server's own ceiling. */
const LIMIT = 200;

export function readFilters(params: URLSearchParams): CaseFilters {
  const state = params.get("state");
  return {
    state: STATES.includes(state as CaseState) ? (state as CaseState) : null,
    package: params.get("package") || null,
  };
}

export function loadCases(filters: CaseFilters): Promise<Case[]> {
  return apiGet("/api/v1/cases", {
    query: { state: filters.state ?? undefined, package: filters.package ?? undefined, limit: LIMIT },
  });
}

const TONE: Record<string, string> = { open: "fail", resolution_selected: "review", funded: "info", closed: "ok" };

export function StatePill({ state }: { state: string }) {
  return <span className={`pill ${TONE[state] ?? "info"}`}>{stateLabel(state)}</span>;
}

export function shortDate(iso: string): string {
  const d = serverTime(iso);
  return d ? d.toISOString().slice(0, 10) : iso;
}

export function CaseTable({ cases, filtered = false }: { cases: Case[]; filtered?: boolean }) {
  if (cases.length === 0) {
    return filtered
      ? <Empty title="No cases match these filters">Clear a filter to see more.</Empty>
      : <Empty title="No cases yet">A case opens from an obsolete or announced finding on the Vendor Intelligence screen.</Empty>;
  }
  return (
    <section className="card" style={{ padding: 0, overflow: "auto" }}>
      <TableScroll label="Cases">
      <table>
        <thead><tr><th>Case</th><th>Tag</th><th>Package</th><th>Finding</th><th>Resolution</th><th>State</th><th>Opened</th></tr></thead>
        <tbody>
          {cases.map((c) => {
            const last = c.resolutions[c.resolutions.length - 1];
            return (
              <tr key={c.id}>
                <td><Link className="mono" to={`/cases/${encodeURIComponent(c.id)}`}>C-{c.id.slice(0, 6).toUpperCase()}</Link></td>
                <td><Link className="mono" to={assetPath(c.package, c.tag)}>{c.tag}</Link></td>
                <td>{c.package}</td>
                <td><Pill value={c.lifecycle} /></td>
                <td>{last ? outcomeLabel(last.outcome) : <span className="muted">none yet</span>}</td>
                <td><StatePill state={c.state} /></td>
                <td className="mono">{shortDate(c.created_at)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </TableScroll>
    </section>
  );
}

export function CasesFailure({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (caseFailure(error) === "off") {
    return <FeatureOff feature="Cases" reason={error instanceof Error ? error.message : null} />;
  }
  return <ErrorState error={error} onRetry={onRetry} />;
}
